import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress } from '@mui/material';
import theme from './theme/theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './routes/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';

// Pages
import Dashboard from './pages/Dashboard';
import Assistant from './pages/Assistant';
import AssistantDetail from './pages/AssistantDetail';
import Projects from './pages/Projects';
import CallLogs from './pages/CallLogs';
import Account from './pages/Account';
import Content from './pages/Content';
import Files from './pages/Files';

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Callback route for Cognito OAuth */}
            <Route path="/callback" element={<CallbackHandler />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="assistant/:id" element={<AssistantDetail />} />
              <Route path="projects" element={<Projects />} />
              <Route path="call-logs" element={<CallLogs />} />
              <Route path="account" element={<Account />} />
              <Route path="content" element={<Content />} />
              <Route path="files" element={<Files />} />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Callback handler for Cognito OAuth
function CallbackHandler() {
  const { login } = useAuth();
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const exchangeCodeForTokens = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (!code) {
        window.location.href = '/';
        return;
      }

      try {
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
        const redirectUri = import.meta.env.VITE_REDIRECT_URI;

        // Exchange authorization code for tokens
        const response = await fetch(`${cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code: code,
            redirect_uri: redirectUri,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Token exchange failed:', response.status, errorText);
          let errorMessage = 'Failed to exchange code for tokens';
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error_description || errorData.error || errorMessage;
          } catch (e) {
            errorMessage = errorText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Store tokens
        const tokens = {
          accessToken: data.access_token,
          idToken: data.id_token,
          refreshToken: data.refresh_token,
        };

        login(tokens);
        window.location.href = '/dashboard';
      } catch (err) {
        console.error('OAuth token exchange error:', err);
        setError(err.message);
        // Redirect to login after error
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    };

    exchangeCodeForTokens();
  }, [login]);

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: '16px' }}>
        <div style={{ color: 'red' }}>Authentication Error: {error}</div>
        <div>Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </div>
  );
}

export default App;
